import styled from "styled-components";
import { useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { IMPRINT_SEO } from '../../seo/pageSeoMeta';
import { purpleColors, violetColors, slateColors, Section, Container } from "./AppLandingPage/styles";

const PageSection = styled(Section)`
  background: linear-gradient(to bottom right, ${slateColors[50]}, white);
  padding-top: 4rem;
  padding-bottom: 5rem;
`;

const PageContainer = styled(Container)`
  max-width: 70rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const HeroCard = styled.div`
  background: linear-gradient(to right, ${purpleColors[50]}, ${violetColors[500]}12);
  border: 1px solid ${purpleColors[100]};
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);

  @media (min-width: 768px) {
    padding: 2.5rem;
  }
`;

const Card = styled.div`
  background: white;
  border: 1px solid ${slateColors[200]};
  border-radius: 1.25rem;
  padding: 1.75rem;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  @media (min-width: 768px) {
    padding: 2.25rem;
    gap: 1.5rem;
  }
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 2.25rem;
  font-weight: 800;
  color: ${slateColors[900]};
  line-height: 1.1;

  @media (min-width: 768px) {
    font-size: 2.75rem;
  }
`;

const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const SectionTitle = styled.h2`
  margin: 0.9rem 0 0.05rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: ${slateColors[900]};

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Paragraph = styled.p`
  margin: 0.1rem 0 0;
  color: ${slateColors[700]};
  font-size: 1rem;
  line-height: 1.65;

  @media (min-width: 768px) {
    font-size: 1.05rem;
  }
`;

const EmailLink = styled.a`
  color: ${purpleColors[600]};
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
  
  &:hover {
    color: ${purpleColors[700]};
    text-decoration: underline;
  }
`;

const ImprintPage = () => {
  const { t } = useTranslation();
  
  // Scroll to top on component mount
  useEffect(() => {
      window.scrollTo(0, 0);
  }, []);

    return (
      <>
        <Helmet prioritizeSeoTags>
          <title>{t('imprint.title')} | chuuchuu</title>
          <meta name="description" content={IMPRINT_SEO.description} />
          <meta name="keywords" content="chuuchuu imprint, company information, legal notice, chuuchuu contact" />
          
          <meta name="prerender-status-code" content="200" />
          <meta name="prerender-header" content="Cache-Control: max-age=300, must-revalidate" />
          <meta name="fragment" content="!" />
          
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://chuuchuu.com/imprint" />
          <meta property="og:title" content={IMPRINT_SEO.title} />
          <meta property="og:description" content={IMPRINT_SEO.description} />
          <meta property="og:image" content="https://chuuchuu.com/images/previewpage.jpg" />
          
          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:url" content="https://chuuchuu.com/imprint" />
          <meta property="twitter:title" content={IMPRINT_SEO.title} />
          <meta property="twitter:description" content={IMPRINT_SEO.description} />
          <meta property="twitter:image" content="https://chuuchuu.com/images/previewpage.jpg" />
          
          <meta name="robots" content="index, follow" />
          <link rel="canonical" href="https://chuuchuu.com/imprint" />
        </Helmet>
      
        <PageSection>
          <PageContainer>
            <HeroCard>
              <PageTitle>{t('imprint.title')}</PageTitle>
            </HeroCard>

            <Card>
              <SectionBlock>
                <SectionTitle>{t('imprint.companyInformation')}</SectionTitle>
                <Paragraph>
                  <strong>chuuchuu BV</strong>
                  <br />
                  {t('imprint.registrationNumber')}: 1030644497
                  <br />
                  <br />
                  Lange Gasthuisstraat 29
                  <br />
                  2000 Antwerpen, Belgium
                </Paragraph>
              </SectionBlock>

              <SectionBlock>
                <SectionTitle>{t('imprint.contact')}</SectionTitle>
                <Paragraph>
                  {t('imprint.email')}: <EmailLink href="mailto:info@chuuchuu.com">info@chuuchuu.com</EmailLink>
                </Paragraph>
              </SectionBlock>
            </Card>
          </PageContainer>
        </PageSection>
      </>
    );
  };
  
  export default ImprintPage;

